import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { appConfig } from '@/config/env'
import type { ApiResponse } from '@/types/api'
import { ApiError as XuntuApiError } from '@/types/api'
import { clearAuthTokens, readAuthTokens, saveAuthTokens } from '@/utils/auth-storage'
import type { AuthTokens } from '@/types/auth'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuthRefresh?: boolean
    authRetry?: boolean
  }
}

const http = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
})

let refreshPromise: Promise<AuthTokens | null> | null = null

/**
 * 鉴权失败业务码（v2 契约）。
 *
 * 后端 v2（对接指南 §0.3 L70-74 + 设计文档顶部）：C 端/管理端业务接口
 * **HTTP 恒为 200**；所有状态（含 401）通过 body 的 `code` 字段表达。
 * `code=401` 统一 msg「请先登录」。旧的 `40101` / `40102` 已删除。
 *
 * 因此：
 *   - 不再判断 `error.response.status === 401`（业务接口 HTTP 不会再是 401）
 *   - 不再 fallback `40102`（已不存在）
 *   - `401` 总是 refreshable（msg 统一，无法区分 access 过期 / refresh 过期——
 *     即使 refresh 失败也会再次返回 401，走清理 token + 跳登录的逻辑）
 */
const AUTH_ERROR_CODE = 401

function isApiEnvelope(value: unknown): value is { code: number; msg?: string } {
  return Boolean(
    value && typeof value === 'object' && 'code' in value && typeof value.code === 'number',
  )
}

function publishAuthExpired(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('xuntu:auth-expired'))
  }
}

export async function refreshAuthToken(): Promise<AuthTokens | null> {
  const current = readAuthTokens()
  if (!current?.refreshToken) return null

  if (!refreshPromise) {
    refreshPromise = http
      .post<ApiResponse<AuthTokens>>(
        '/app/auth/refresh',
        { refreshToken: current.refreshToken },
        { skipAuthRefresh: true },
      )
      .then(({ data }) => {
        if (data.code !== 200) {
          throw new XuntuApiError(data.msg || '登录状态已过期', data.code)
        }
        saveAuthTokens(data.data)
        return data.data
      })
      .catch(() => null)
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

interface AuthFailureContext {
  code: number
  msg?: string
  status?: number
  config?: AxiosRequestConfig
}

/**
 * 统一处理鉴权失败：可刷新则换新双 token 重发原请求，否则清理并广播 `xuntu:auth-expired`。
 * `skipAuthRefresh`（`/app/auth/refresh` 自身）不广播事件，由外层请求兜底跳登录。
 */
async function handleAuthFailure(ctx: AuthFailureContext): Promise<AxiosResponse> {
  const { code, msg, status, config } = ctx
  const canRefresh =
    Boolean(config) && code === AUTH_ERROR_CODE && !config?.skipAuthRefresh && !config?.authRetry

  if (canRefresh) {
    const tokens = await refreshAuthToken()
    if (tokens) {
      return http.request({
        ...config,
        authRetry: true,
        headers: {
          ...config?.headers,
          Authorization: `${tokens.tokenType || 'Bearer'} ${tokens.accessToken}`,
        },
      })
    }
  }

  clearAuthTokens()
  if (!config?.skipAuthRefresh) publishAuthExpired()
  throw new XuntuApiError(msg || '登录状态已过期', code, status, config?.url)
}

http.interceptors.request.use((config) => {
  const tokens = readAuthTokens()
  if (tokens?.accessToken) {
    config.headers.Authorization = `${tokens.tokenType || 'Bearer'} ${tokens.accessToken}`
  }
  return config
})

http.interceptors.response.use(
  async (response) => {
    const body: unknown = response.data
    if (!isApiEnvelope(body)) return response

    if (body.code === AUTH_ERROR_CODE) {
      return handleAuthFailure({
        code: body.code,
        msg: body.msg,
        status: response.status,
        config: response.config,
      })
    }

    if (body.code !== 200) {
      throw new XuntuApiError(
        body.msg || '请求失败',
        body.code,
        response.status,
        response.config.url,
      )
    }

    return response
  },
  async (error: AxiosError<ApiResponse<unknown>>): Promise<AxiosResponse> => {
    const status = error.response?.status
    const body = error.response && isApiEnvelope(error.response.data) ? error.response.data : null

    // 业务接口 HTTP 恒 200（v2 契约）。错误路径上若 body.code=401 仍走鉴权失败；
    // 网络/网关层 401（如 OAuth 例外路径）也归类为鉴权失败。
    if (body && body.code === AUTH_ERROR_CODE) {
      return handleAuthFailure({
        code: body.code,
        msg: body.msg,
        status,
        config: error.config,
      })
    }
    if (status === 401) {
      return handleAuthFailure({
        code: AUTH_ERROR_CODE,
        msg: body?.msg,
        status,
        config: error.config,
      })
    }

    if (body) {
      throw new XuntuApiError(body.msg || '请求失败', body.code, status, error.config?.url)
    }
    throw new XuntuApiError('网络异常，请检查连接', 0, status, error.config?.url)
  },
)

export default http
